import { useCallback, useEffect, useState } from 'react';
import { authApi, CurrentUser } from '../services/api';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
  subscribeAuthInvalidated,
} from '../services/authStorage';

export const useAuth = () => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  /** 微信新用户首次登录后需要绑定用户名密码 */
  const [needBindInfo, setNeedBindInfo] = useState(false);

  useEffect(() => {
    let isActive = true;
    let isUnmounting = false;

    const clearUser = () => {
      if (isActive) {
        setUser(null);
        setIsLoading(false);
      }
    };
    const unsubscribe = subscribeAuthInvalidated(clearUser);

    if (!getAccessToken()) {
      clearUser();
      return () => {
        isActive = false;
        isUnmounting = true;
        unsubscribe();
      };
    }

    authApi.getCurrentUser()
      .then((response) => {
        if (isActive) {
          setUser(response.data.data);
        }
      })
      .catch((error) => {
        // 如果是组件卸载导致的错误，不清除token
        if (isUnmounting) {
          return;
        }
        // 如果是网络错误（如ERR_ABORTED），也不清除token
        if (error?.code === 'ERR_ABORTED' || error?.name === 'AbortError') {
          if (isActive) {
            setIsLoading(false);
          }
          return;
        }
        // 只有真正的认证错误（如401）才清除token
        if (error?.response?.status === 401) {
          clearAccessToken();
          clearUser();
        } else {
          // 其他网络错误，保持token但结束loading
          if (isActive) {
            setIsLoading(false);
          }
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
      isUnmounting = true;
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    setAccessToken(response.data.data.access_token);

    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser.data.data);
    } catch (error) {
      clearAccessToken();
      throw error;
    }
  }, []);

  /** 微信扫码登录（zbapi JWT 模式）。
   *
   * zbapi 平台已升级，扫码后直接返回 HS256 JWT（payload 含 sub=member_id）。
   * 前端轮询 zbapi 拿到 JWT 后，调用本方法用 zbapi JWT 换取 aibiaoshu JWT。
   */
  const wechatLoginByJwt = useCallback(async (zbapi_jwt: string) => {
    const response = await authApi.wechatLoginByJwt(zbapi_jwt);
    const { access_token, need_bind_info } = response.data.data;
    setAccessToken(access_token);
    setNeedBindInfo(!!need_bind_info);

    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser.data.data);
    } catch (error) {
      clearAccessToken();
      setNeedBindInfo(false);
      throw error;
    }
  }, []);

  /** 微信新用户绑定用户名密码 */
  const bindInfo = useCallback(async (username: string, password: string) => {
    await authApi.bindInfo(username, password);
    setNeedBindInfo(false);
    // 刷新用户信息，让 username 同步到 user 对象
    const currentUser = await authApi.getCurrentUser();
    setUser(currentUser.data.data);
  }, []);

  const logout = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setNeedBindInfo(false);
  }, []);

  return {
    user,
    isLoading,
    login,
    wechatLoginByJwt,
    bindInfo,
    needBindInfo,
    setNeedBindInfo,
    logout,
  };
};
